import { PageHeader } from "../../../components/ui";
import { FormSelect } from "../../../components/ui/select";
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppLayout, PageBackLink } from '../../../components/layout';
import '../inventory.css';
import {
  fetchWarehouseById,
  createWarehouse,
  updateWarehouse,
  fetchWarehouseFormLookups,
  formatWarehouseTypeLabel,
  isProjectSiteWarehouseType,
} from '../../../services/warehouseService';
import i18n from "../../../i18n";
import { Trans } from "react-i18next";

const TOAST_KEY = 'warehouse_list_toast';

const EMPTY_FORM = {
  name: '',
  code: '',
  type: '',
  warehouseTypeId: '',
  status: 'Active',
  primaryHub: 'No',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  pincode: '',
  manager: '',
  managerId: '',
  customerId: '',
  customerName: '',
  fifoFefoSupport: 'Both',
  documents: [],
};

export default function AddEditWarehouse() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [warehouseTypes, setWarehouseTypes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [lookupsLoading, setLookupsLoading] = useState(true);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  // const [dragActive, setDragActive] = useState(false);

  const isProjectSite = isProjectSiteWarehouseType(formData.type);

  const managerOptions = useMemo(() => {
    const list = [...employees];
    if (
      formData.managerId &&
      !list.some((emp) => String(emp.id) === String(formData.managerId))
    ) {
      list.unshift({
        id: String(formData.managerId),
        name: formData.manager || `Employee #${formData.managerId}`,
        code: String(formData.managerId),
        designation: 'Inventory Manager',
      });
    }
    return list;
  }, [employees, formData.managerId, formData.manager]);

  const customerOptions = useMemo(() => {
    const list = [...customers];
    if (
      formData.customerId &&
      !list.some((c) => String(c.id) === String(formData.customerId))
    ) {
      list.unshift({
        id: String(formData.customerId),
        name: formData.customerName || `Customer #${formData.customerId}`,
      });
    }
    return list;
  }, [customers, formData.customerId, formData.customerName]);

  useEffect(() => {
    let cancelled = false;
    setLookupsLoading(true);
    fetchWarehouseFormLookups({ size: 10 })
      .then((lookups) => {
        if (cancelled) return;
        setWarehouseTypes(lookups.warehouseTypes || []);
        setEmployees(lookups.employees || []);
        setCustomers(lookups.customers || []);
      })
      .catch(() => {
        if (cancelled) return;
        setWarehouseTypes([]);
        setEmployees([]);
        setCustomers([]);
      })
      .finally(() => {
        if (!cancelled) setLookupsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isEdit || !id) return undefined;
    let cancelled = false;

    async function load() {
      setLoading(true);
      setLoadError(null);
      try {
        const existing = await fetchWarehouseById(id);
        if (cancelled) return;
        if (!existing) {
          navigate('/IMS/inventory/locations');
          return;
        }
        setFormData({
          name: existing.name || '',
          code: existing.code || '',
          type: existing.type || '',
          warehouseTypeId: existing.warehouseTypeId || '',
          status: existing.status || 'Active',
          primaryHub: existing.primaryHub || 'No',
          addressLine1: existing.addressLine1 || '',
          addressLine2: existing.addressLine2 || '',
          city: existing.city || '',
          state: existing.state || '',
          pincode: existing.pincode || '',
          manager: existing.manager || '',
          managerId: existing.managerId || '',
          customerId: existing.customerId || '',
          customerName: existing.customerName || '',
          fifoFefoSupport: existing.fifoFefoSupport || 'Both',
          documents: existing.documents || [],
        });
      } catch (err) {
        if (cancelled) return;
        setLoadError(err?.message || i18n.t('inventory.failedToLoadWarehouse'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit, navigate]);

  // Default type / manager once lookups arrive (create mode only).
  useEffect(() => {
    if (isEdit || lookupsLoading) return;
    setFormData((prev) => {
      const next = { ...prev };
      let changed = false;
      if (!next.warehouseTypeId && warehouseTypes.length > 0) {
        next.warehouseTypeId = warehouseTypes[0].id;
        next.type = warehouseTypes[0].name;
        changed = true;
      }
      if (!next.managerId && employees.length > 0) {
        next.managerId = employees[0].id;
        next.manager = employees[0].name;
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [isEdit, lookupsLoading, warehouseTypes, employees]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'warehouseTypeId') {
      const selected = warehouseTypes.find((t) => String(t.id) === String(value));
      const nextType = selected?.name || '';
      const nextIsProjectSite = isProjectSiteWarehouseType(nextType);
      setFormData((prev) => ({
        ...prev,
        warehouseTypeId: value,
        type: nextType,
        ...(nextIsProjectSite
          ? {}
          : { customerId: '', customerName: '' }),
      }));
    } else if (name === 'managerId') {
      const selected = employees.find((emp) => String(emp.id) === String(value));
      setFormData((prev) => ({
        ...prev,
        managerId: value,
        manager: selected?.name || '',
      }));
    } else if (name === 'customerId') {
      const selected = customers.find((c) => String(c.id) === String(value));
      setFormData((prev) => ({
        ...prev,
        customerId: value,
        customerName: selected?.name || '',
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    if (
      errors[name] ||
      (name === 'warehouseTypeId' && (errors.type || errors.customerId)) ||
      (name === 'managerId' && errors.manager)
    ) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
        ...(name === 'warehouseTypeId' ? { type: null, customerId: null } : {}),
        ...(name === 'managerId' ? { manager: null } : {}),
      }));
    }
  };

  // const handleFileUpload = (files) => {
  //   if (!files || files.length === 0) return;
  //   const uploadedDocs = Array.from(files).map((f) => ({
  //     id: `doc-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
  //     name: f.name,
  //     size: `${(f.size / (1024 * 1024)).toFixed(1)} MB`,
  //     uploadDate: new Date().toISOString().split('T')[0],
  //   }));
  //   setFormData((prev) => ({
  //     ...prev,
  //     documents: [...prev.documents, ...uploadedDocs],
  //   }));
  // };

  // const handleRemoveDoc = (docId) => {
  //   setFormData((prev) => ({
  //     ...prev,
  //     documents: prev.documents.filter((d) => d.id !== docId),
  //   }));
  // };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = i18n.t('inventory.warehouseNameRequired');
    if (!formData.code.trim()) newErrors.code = i18n.t('inventory.warehouseCodeRequired');
    if (!formData.warehouseTypeId) newErrors.type = i18n.t('inventory.warehouseTypeRequired');
    if (!formData.status) newErrors.status = i18n.t('inventory.statusRequired');
    if (!formData.addressLine1.trim()) newErrors.addressLine1 = i18n.t('inventory.addressLine1Required');
    if (!formData.addressLine2.trim()) newErrors.addressLine2 = i18n.t('inventory.addressLine2Required');
    if (!formData.city.trim()) newErrors.city = i18n.t('inventory.cityRequired');
    if (!formData.state.trim()) newErrors.state = i18n.t('inventory.stateRequired');
    if (!formData.pincode.trim()) newErrors.pincode = i18n.t('inventory.pincodeRequired');

    if (!formData.managerId) {
      if (employees.length === 0) {
        newErrors.manager = i18n.t('inventory.noEligibleEmployeesExist');
      } else {
        newErrors.manager = i18n.t('inventory.inventoryManagerMandatory');
      }
    }

    if (isProjectSiteWarehouseType(formData.type) && !formData.customerId) {
      if (customers.length === 0) {
        newErrors.customerId = i18n.t('inventory.noCustomersFound');
      } else {
        newErrors.customerId = i18n.t('inventory.customerRequired');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    if (!validate()) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setSaving(true);
    try {
      const savedRecord = isEdit
        ? await updateWarehouse(id, formData)
        : await createWarehouse(formData);

      const displayName = savedRecord?.name || formData.name;
      sessionStorage.setItem(
        TOAST_KEY,
        isEdit
          ? i18n.t('inventory.warehouseUpdatedSuccess', { name: displayName })
          : i18n.t('inventory.warehouseCreatedSuccess', { name: displayName })
      );
      navigate('/IMS/inventory/locations');
    } catch (err) {
      setSubmitError(
        err?.message ||
          (isEdit
            ? i18n.t('inventory.failedToUpdateWarehouse')
            : i18n.t('inventory.failedToCreateWarehouse'))
      );
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  if (loading || lookupsLoading) {
    return (
      <AppLayout formLayout="standard">
        <div className="inventory-module" style={{ padding: '48px 20px', textAlign: 'center', color: '#64748b' }}>
          {loading ? i18n.t('inventory.loadingWarehouse') : i18n.t('inventory.loadingFormOptions')}
        </div>
      </AppLayout>
    );
  }

  if (loadError) {
    return (
      <AppLayout formLayout="standard">
        <div className="inventory-module" style={{ padding: '32px 20px' }}>
          <div
            style={{
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '12px',
              padding: '16px 20px',
              color: '#991b1b',
              fontSize: '13px',
            }}
          >
            <p style={{ margin: '0 0 12px 0' }}>{loadError}</p>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => navigate('/IMS/inventory/locations')}
            >
              <Trans i18nKey="inventory.backToWarehouses" />
            </button>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout formLayout="standard">
      <div className="inventory-module">

        <PageHeader back={<PageBackLink to="/IMS/inventory/locations" />} title={isEdit ? i18n.t("inventory.editWarehouseTitle") : i18n.t("inventory.addWarehouseTitle")} />

        {submitError && (
          <div
            style={{
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '12px',
              padding: '12px 16px',
              marginBottom: '16px',
              color: '#991b1b',
              fontSize: '13px',
            }}
          >
            {submitError}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-surface form-section"
            style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}
          >
            <div>
              <h3
                style={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#152273',
                  marginBottom: '16px',
                  paddingBottom: '8px',
                  borderBottom: '1px solid #f1f5f9',
                }}
              >
                <Trans i18nKey="inventory.basicInformationNumbered" />
              </h3>
              <div className="inv-responsive-grid--3" style={{ gap: '20px' }}>
                <div className="form-group">
                  <label className="form-label" style={{ fontWeight: '600', color: '#334155' }}>
                    <Trans i18nKey="inventory.warehouseName" /> <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    className="form-input"
                    placeholder={i18n.t('inventory.warehouseNamePlaceholder')}
                    value={formData.name}
                    onChange={handleChange}
                    style={{ borderColor: errors.name ? '#ef4444' : undefined }}
                  />
                  {errors.name && <span style={{ color: '#ef4444', fontSize: '12px' }}>{errors.name}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label" style={{ fontWeight: '600', color: '#334155' }}>
                    <Trans i18nKey="inventory.warehouseCode" /> <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="code"
                    className="form-input"
                    placeholder={i18n.t('inventory.warehouseCodePlaceholder')}
                    value={formData.code}
                    onChange={handleChange}
                    style={{ borderColor: errors.code ? '#ef4444' : undefined }}
                  />
                  {errors.code && <span style={{ color: '#ef4444', fontSize: '12px' }}>{errors.code}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label" style={{ fontWeight: '600', color: '#334155' }}>
                    <Trans i18nKey="inventory.warehouseType" /> <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  {warehouseTypes.length > 0 ? (
                    <FormSelect
                      name="warehouseTypeId"
                      className="form-select"
                      value={formData.warehouseTypeId}
                      onChange={handleChange}
                      style={{ borderColor: errors.type ? '#ef4444' : undefined }}
                    >
                      <option value="">{i18n.t('fsm.selectType')}</option>
                      {warehouseTypes.map((t) => (
                        <option key={t.id} value={t.id}>
                          {formatWarehouseTypeLabel(t.name)}
                        </option>
                      ))}
                    </FormSelect>
                  ) : (
                    <div
                      style={{
                        padding: '10px 14px',
                        backgroundColor: '#fef2f2',
                        border: '1px solid #fca5a5',
                        borderRadius: '8px',
                        color: '#991b1b',
                        fontSize: '13px',
                      }}
                    >
                      <Trans i18nKey="inventory.noWarehouseTypesFound" />
                    </div>
                  )}
                  {errors.type && <span style={{ color: '#ef4444', fontSize: '12px' }}>{errors.type}</span>}
                </div>

                {isProjectSite && (
                  <div className="form-group">
                    <label className="form-label" style={{ fontWeight: '600', color: '#334155' }}>
                      <Trans i18nKey="common.customer" /> <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    {customerOptions.length > 0 ? (
                      <FormSelect
                        name="customerId"
                        className="form-select"
                        value={formData.customerId}
                        onChange={handleChange}
                        style={{ borderColor: errors.customerId ? '#ef4444' : undefined }}
                      >
                        <option value="">{i18n.t('inventory.selectCustomer')}</option>
                        {customerOptions.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </FormSelect>
                    ) : (
                      <div
                        style={{
                          padding: '10px 14px',
                          backgroundColor: '#fef2f2',
                          border: '1px solid #fca5a5',
                          borderRadius: '8px',
                          color: '#991b1b',
                          fontSize: '13px',
                        }}
                      >
                        <Trans i18nKey="inventory.noCustomersFound" />
                      </div>
                    )}
                    {errors.customerId && (
                      <span style={{ color: '#ef4444', fontSize: '12px' }}>{errors.customerId}</span>
                    )}
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label" style={{ fontWeight: '600', color: '#334155' }}>
                    <Trans i18nKey="status.status" /> <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <FormSelect
                    name="status"
                    className="form-select"
                    value={formData.status}
                    onChange={handleChange}
                    style={{ borderColor: errors.status ? '#ef4444' : undefined }}
                  >
                    <option value="Active">{i18n.t('status.active')}</option>
                    <option value="Inactive">{i18n.t('status.inactive')}</option>
                  </FormSelect>
                  {errors.status && <span style={{ color: '#ef4444', fontSize: '12px' }}>{errors.status}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label" style={{ fontWeight: '600', color: '#334155' }}>
                    <Trans i18nKey="inventory.hubWarehouseQuestion" />
                  </label>
                  <FormSelect
                    name="primaryHub"
                    className="form-select"
                    value={formData.primaryHub}
                    onChange={handleChange}
                  >
                    <option value="No">{i18n.t('common.no')}</option>
                    <option value="Yes">{i18n.t('common.yes')}</option>
                  </FormSelect>
                </div>
              </div>
            </div>

            <div>
              <h3
                style={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#152273',
                  marginBottom: '16px',
                  paddingBottom: '8px',
                  borderBottom: '1px solid #f1f5f9',
                }}
              >
                <Trans i18nKey="inventory.locationNumbered" />
              </h3>
              <div className="inv-responsive-grid--3" style={{ gap: '20px' }}>
                <div className="form-group">
                  <label className="form-label" style={{ fontWeight: '600', color: '#334155' }}>
                    <Trans i18nKey="inventory.addressLine1Label" /> <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="addressLine1"
                    className="form-input"
                    placeholder={i18n.t('inventory.buildingStreetPlaceholder')}
                    value={formData.addressLine1}
                    onChange={handleChange}
                    style={{ borderColor: errors.addressLine1 ? '#ef4444' : undefined }}
                  />
                  {errors.addressLine1 && (
                    <span style={{ color: '#ef4444', fontSize: '12px' }}>{errors.addressLine1}</span>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label" style={{ fontWeight: '600', color: '#334155' }}>
                    <Trans i18nKey="inventory.addressLine2Label" /> <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="addressLine2"
                    className="form-input"
                    placeholder={i18n.t('inventory.areaLandmarkPlaceholder')}
                    value={formData.addressLine2}
                    onChange={handleChange}
                    style={{ borderColor: errors.addressLine2 ? '#ef4444' : undefined }}
                  />
                  {errors.addressLine2 && (
                    <span style={{ color: '#ef4444', fontSize: '12px' }}>{errors.addressLine2}</span>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label" style={{ fontWeight: '600', color: '#334155' }}>
                    <Trans i18nKey="crm.city" />
                  </label>
                  <input
                    type="text"
                    name="city"
                    className="form-input"
                    placeholder={i18n.t('inventory.cityPlaceholderCoimbatore')}
                    value={formData.city}
                    onChange={handleChange}
                    style={{ borderColor: errors.city ? '#ef4444' : undefined }}
                  />
                  {errors.city && <span style={{ color: '#ef4444', fontSize: '12px' }}>{errors.city}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label" style={{ fontWeight: '600', color: '#334155' }}>
                    <Trans i18nKey="crm.state" /> <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="state"
                    className="form-input"
                    placeholder={i18n.t('inventory.statePlaceholderTamilNadu')}
                    value={formData.state}
                    onChange={handleChange}
                    style={{ borderColor: errors.state ? '#ef4444' : undefined }}
                  />
                  {errors.state && <span style={{ color: '#ef4444', fontSize: '12px' }}>{errors.state}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label" style={{ fontWeight: '600', color: '#334155' }}>
                    <Trans i18nKey="inventory.pinCode" /> <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="pincode"
                    className="form-input"
                    placeholder={i18n.t('inventory.pinPlaceholder641004')}
                    value={formData.pincode}
                    onChange={handleChange}
                    style={{ borderColor: errors.pincode ? '#ef4444' : undefined }}
                  />
                  {errors.pincode && <span style={{ color: '#ef4444', fontSize: '12px' }}>{errors.pincode}</span>}
                </div>
              </div>
            </div>

            <div className="inv-responsive-grid--2 inv-warehouse-contact-ops" style={{ gap: '24px', alignItems: 'start' }}>
              <div>
                <h3
                  style={{
                    fontSize: '16px',
                    fontWeight: '700',
                    color: '#152273',
                    marginBottom: '16px',
                    paddingBottom: '8px',
                    borderBottom: '1px solid #f1f5f9',
                  }}
                >
                  <Trans i18nKey="inventory.contactsNumbered" />
                </h3>
                <div className="form-group">
                  <label className="form-label" style={{ fontWeight: '600', color: '#334155' }}>
                    <Trans i18nKey="inventory.inventoryManager" /> <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  {managerOptions.length > 0 ? (
                    <FormSelect
                      name="managerId"
                      className="form-select"
                      value={formData.managerId}
                      onChange={handleChange}
                      style={{ borderColor: errors.manager ? '#ef4444' : undefined }}
                    >
                      <option value="">{i18n.t('inventory.selectEmployee')}</option>
                      {managerOptions.map((emp) => (
                        <option key={emp.id} value={emp.id}>
                          {emp.name} ({emp.code}{emp.designation ? ` — ${emp.designation}` : ''})
                        </option>
                      ))}
                    </FormSelect>
                  ) : (
                    <div
                      style={{
                        padding: '10px 14px',
                        backgroundColor: '#fef2f2',
                        border: '1px solid #fca5a5',
                        borderRadius: '8px',
                        color: '#991b1b',
                        fontSize: '13px',
                      }}
                    >
                      <Trans i18nKey="inventory.noEligibleEmployeesFound" />
                    </div>
                  )}
                  {errors.manager && <span style={{ color: '#ef4444', fontSize: '12px' }}>{errors.manager}</span>}
                </div>
              </div>

              <div>
                <h3
                  style={{
                    fontSize: '16px',
                    fontWeight: '700',
                    color: '#152273',
                    marginBottom: '16px',
                    paddingBottom: '8px',
                    borderBottom: '1px solid #f1f5f9',
                  }}
                >
                  <Trans i18nKey="inventory.operationsNumbered" />
                </h3>
                <div className="form-group">
                  <label className="form-label" style={{ fontWeight: '600', color: '#334155' }}>
                    <Trans i18nKey="inventory.dispatchStrategyFifoFefo" />
                  </label>
                  <FormSelect
                    name="fifoFefoSupport"
                    className="form-select"
                    value={formData.fifoFefoSupport}
                    onChange={handleChange}
                  >
                    <option value="Both">{i18n.t('onboarding.both')}</option>
                    <option value="FIFO">{i18n.t('inventory.fifoLabel')}</option>
                    <option value="FEFO">{i18n.t('inventory.fefoLabel')}</option>
                  </FormSelect>
                </div>
              </div>
            </div>

            <div className="form-buttons-right">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => navigate('/IMS/inventory/locations')}
                disabled={saving}
                style={{ height: '42px', padding: '0 24px' }}
              >
                <Trans i18nKey="formActions.cancel" />
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={saving}
                style={{ height: '42px', padding: '0 28px', backgroundColor: '#152273', borderRadius: '6px' }}
              >
                {saving ? i18n.t('inventory.savingEllipsis') : i18n.t('inventory.saveWarehouse')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </AppLayout>
  );
}
